/* global t, safeArray, num, pkMoney */
// PERIODEKONTROLLER — kontrollene lesemodellen utleder
//
// Skilt fra backend-period-recon-view.jsx, som setter periodebildet sammen.
// Her ligger de fire utledningene som SIER NOE om perioden: hovedkontrollene,
// tverrkontrollene, de systemutledede sjekklistepunktene og blokkeringene.
//
// Ingen av dem lagres. De leses av områdenes egen status hver gang skjermen
// tegnes, slik at de aldri kan si noe annet enn avstemmingene de bygger på.

const pkRound2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
const PK_CHECK_TOLERANCE = 0.005;
// ------------------------------------------------------------------
// Automatisk avledede hovedkontroller
// ------------------------------------------------------------------
// Seks påstander om perioden, hver med kildene den bygger på. De er ikke en
// egen kontroll — de er en OPPSUMMERING av områdene, og skal derfor aldri kunne
// si noe annet enn områdene selv.
const pkMasterControls = ({ rows, blockers }) => {
  const byKey = Object.fromEntries(rows.map((row) => [row.key, row]));
  const group = (keys) => keys.map((key) => byKey[key]).filter(Boolean);
  const resultFor = (entries) => {
    if (!entries.length) return 'ikke_vurdert';
    if (entries.some((row) => row.status === 'avvik')) return 'avvik';
    if (entries.some((row) => row.status === 'venter_periodeslutt')) return 'venter';
    if (entries.every((row) => ['ok', 'ikke_relevant'].includes(row.status))) return 'bestatt';
    return 'avvik';
  };
  const kilde = (entries) => entries.map((row) => row.label + ': ' + row.statusLabel);

  const controls = [
    { id: 'bank', label: t('rcd.master.bank', 'Alle bankkontoer er avstemt'), rows: group(['bank']) },
    { id: 'reskontro', label: t('rcd.master.subledger', 'Kunde- og leverandørreskontro er avstemt'), rows: group(['kundefordringer', 'leverandorgjeld']) },
    { id: 'mva', label: t('rcd.master.vat', 'MVA er kontrollert'), rows: group(['mva']) },
    { id: 'lonn', label: t('rcd.master.payroll', 'Lønn og offentlige avgifter er kontrollert'), rows: group(['lonn', 'skatt']) },
  ].map((control) => ({
    id: control.id,
    label: control.label,
    kilde: kilde(control.rows),
    resultat: resultFor(control.rows),
  }));

  const missingDocs = rows.filter((row) => row.missingDocs > 0);
  controls.push({
    id: 'dok',
    label: t('rcd.master.docs', 'Dokumentasjonen er komplett'),
    kilde: missingDocs.length
      ? missingDocs.map((row) => t('rcd.master.docsSource', '{area}: {count} konto(er) uten dokumentasjon', { area: row.label, count: row.missingDocs }))
      : [t('rcd.master.docsAllOk', 'Alle kontoer som krever et eksternt grunnlag har dokumentasjon eller et notat')],
    resultat: missingDocs.length ? 'avvik' : 'bestatt',
    tekst: missingDocs.length
      ? t('rcd.master.docsText', 'Avvik – {count} dokumentkrav mangler', { count: missingDocs.reduce((sum, row) => sum + row.missingDocs, 0) })
      : null,
  });

  controls.push({
    id: 'rapportering',
    label: t('rcd.master.reporting', 'Perioden er klar til rapportering'),
    kilde: [],
    resultat: blockers.length ? 'blokkert' : 'bestatt',
    begrunnelse: blockers.map((blocker) => blocker.text),
  });

  return controls;
};

// ------------------------------------------------------------------
// Tverrkontroller
// ------------------------------------------------------------------
// Kontroller som går på tvers av flere områder. To av designets tolv er utelatt
// med vilje: «alle integrasjoner er synkronisert til samme skjæringsdato» og
// «ingen rapporter bygger på eldre hovedboksdata» krever et
// synkroniseringsspor Plumme ikke fører. En kontroll uten grunnlag som alltid
// står grønn er verre enn ingen kontroll.
const pkCrossChecks = ({ rows, accounts, ledgerBalance, confirmations = {} }) => {
  const byKey = Object.fromEntries(rows.map((row) => [row.key, row]));
  const areaResult = (keys) => {
    const entries = keys.map((key) => byKey[key]).filter(Boolean);
    const unexplained = entries.reduce((sum, row) => sum + row.unexplained, 0);
    return {
      avvikText: unexplained > PK_CHECK_TOLERANCE ? pkMoney(unexplained) + ' kr' : '0 kr',
      resultat: unexplained > PK_CHECK_TOLERANCE ? 'avvik' : 'bestatt',
      resultatTekst: unexplained > PK_CHECK_TOLERANCE ? t('rcd.cross.unexplainedDiff', 'Uforklart differanse') : t('rcd.cross.matches', 'Stemmer'),
      areaKey: keys[0],
    };
  };

  const checks = [];
  if (ledgerBalance !== null) {
    checks.push({
      id: 'hovedbok', regel: t('rcd.cross.ledgerRule', 'Hovedboken balanserer med like summer i debet og kredit.'),
      kilder: t('rcd.cross.srcLedger', 'Hovedbok'), type: 'auto',
      resultatTekst: Math.abs(ledgerBalance) > PK_CHECK_TOLERANCE ? t('rcd.cross.ledgerOff', 'Debet og kredit går ikke opp') : t('rcd.cross.matches', 'Stemmer'),
      avvik: pkMoney(Math.abs(ledgerBalance)) + ' kr',
      resultat: Math.abs(ledgerBalance) > PK_CHECK_TOLERANCE ? 'avvik' : 'bestatt',
    });
  }
  const pairs = [
    { id: 'bank', regel: t('rcd.cross.bankRule', 'Banksaldoer stemmer med bankavstemmingene.'), kilder: t('rcd.cross.srcBank', 'Bank, hovedbok'), keys: ['bank'] },
    { id: 'reskontro', regel: t('rcd.cross.subledgerRule', 'Kunde- og leverandørreskontro stemmer med hovedboken.'), kilder: t('rcd.cross.srcSubledger', 'Kundefordringer, leverandørgjeld, hovedbok'), keys: ['kundefordringer', 'leverandorgjeld'] },
    { id: 'mva', regel: t('rcd.cross.vatRule', 'MVA-grunnlaget stemmer med relevante hovedbokskontoer.'), kilder: t('rcd.cross.srcVat', 'MVA-avstemming, hovedbok'), keys: ['mva'] },
    { id: 'lonn', regel: t('rcd.cross.payrollRule', 'Lønnsgrunnlaget stemmer med lønnskjøring og hovedbok.'), kilder: t('rcd.cross.srcPayroll', 'Lønnssystem, hovedbok, A-melding'), keys: ['lonn'] },
    { id: 'skatt', regel: t('rcd.cross.taxRule', 'Skattetrekk og offentlige avgifter stemmer med lønns- og avgiftsgrunnlaget.'), kilder: t('rcd.cross.srcTax', 'Lønnssystem, skattekonto'), keys: ['skatt'] },
  ];
  for (const pair of pairs) {
    const row = byKey[pair.keys[0]];
    if (!row || !row.total) continue;
    const result = areaResult(pair.keys);
    const waiting = row.status === 'venter_periodeslutt';
    checks.push({
      id: pair.id, regel: pair.regel, kilder: pair.kilder, type: 'auto',
      resultatTekst: waiting ? t('rcd.cross.waitingPeriodEnd', 'Venter på periodeslutt') : result.resultatTekst,
      avvik: waiting ? '—' : result.avvikText,
      resultat: waiting ? 'venter' : result.resultat,
      areaKey: pair.keys[0],
    });
  }

  // Eiendelskontoer med negativ saldo. Fortegnet alene sier ikke at noe er
  // galt — en negativ eiendel kan være en riktig postering — men den skal
  // være sett på, og det er en faglig vurdering, ikke en automatisk.
  const negativeAssets = safeArray(accounts)
    .filter((account) => account.category === 'asset' && num(account.bookedBalance) < -PK_CHECK_TOLERANCE);
  checks.push({
    id: 'fortegn', regel: t('rcd.cross.signRule', 'Eiendelskontoer med negativ saldo er undersøkt.'), kilder: t('rcd.cross.srcTrialBalance', 'Saldobalanse'), type: 'faglig',
    resultatTekst: negativeAssets.length ? t('rcd.cross.negativeAccounts', '{count} konto(er) med negativ saldo', { count: negativeAssets.length }) : t('rcd.cross.noneFound', 'Ingen funnet'),
    avvik: negativeAssets.length ? pkMoney(negativeAssets.reduce((sum, account) => sum + Math.abs(num(account.bookedBalance)), 0)) + ' kr' : '—',
    resultat: negativeAssets.length ? 'avvik' : 'bestatt',
  });

  const ovrig = byKey.ovrig;
  if (ovrig && ovrig.total) {
    checks.push({
      id: 'uavklart', regel: t('rcd.cross.unclearedRule', 'Midlertidige og uavklarte kontoer er gjennomgått.'), kilder: t('rcd.cross.srcOther', 'Øvrig balanse'), type: 'faglig',
      resultatTekst: ovrig.apnePoster ? t('rcd.cross.openItems', '{count} åpne poster', { count: ovrig.apnePoster }) : t('rcd.cross.noOpenItems', 'Ingen åpne poster'),
      avvik: ovrig.unexplained > PK_CHECK_TOLERANCE ? pkMoney(ovrig.unexplained) + ' kr' : '—',
      resultat: ovrig.apnePoster ? 'avvik' : 'bestatt',
      areaKey: 'ovrig',
    });
  }

  const openItems = rows.reduce((sum, row) => sum + row.apnePoster, 0);
  checks.push({
    id: 'poster', regel: t('rcd.cross.itemsRule', 'Alle vesentlige avstemmingsposter er behandlet eller dokumentert for oppfølging.'), kilder: t('rcd.cross.srcAllAreas', 'Alle områder'), type: 'auto',
    resultatTekst: openItems ? t('rcd.cross.itemsStillOpen', '{count} poster fortsatt åpne', { count: openItems }) : t('rcd.cross.allItemsDone', 'Alle poster er behandlet'),
    avvik: openItems ? t('rcd.cross.itemsCount', '{count} poster', { count: openItems }) : '—',
    resultat: openItems ? 'avvik' : 'bestatt',
  });

  const changed = rows.filter((row) => row.basisChanged);
  checks.push({
    id: 'endret', regel: t('rcd.cross.changedRule', 'Ingen godkjent avstemming er endret uten ny kontroll.'), kilder: t('rcd.cross.srcActivityLog', 'Aktivitetslogg'), type: 'auto',
    resultatTekst: changed.length ? t('rcd.cross.changedAfterApproval', '{areas} er endret etter godkjenning', { areas: changed.map((row) => row.label).join(', ') }) : t('rcd.cross.matches', 'Stemmer'),
    avvik: changed.length ? t('rcd.cross.areasCount', '{count} områder', { count: changed.length }) : '—',
    resultat: changed.length ? 'avvik' : 'bestatt',
  });

  return checks.map((check) => ({
    ...check,
    bekreftetAv: confirmations?.[check.id]?.bekreftetAv || '',
    bekreftetAt: confirmations?.[check.id]?.updatedAt || null,
  }));
};

// ------------------------------------------------------------------
// Faglig sjekkliste
// ------------------------------------------------------------------
// De systemutledede punktene lages her og lagres aldri. De faglige punktene
// kommer fra områdets egen sjekkliste i backend, som er der arbeidet lagres.
// Skillet er hele poenget: et hakk på et systemutledet punkt ville kunnet si
// noe annet enn avstemmingen det bygger på.
const pkAutoChecks = ({ rows, completeness, analytics, reports, blockers, areaCount }) => {
  const byKey = Object.fromEntries(rows.map((row) => [row.key, row]));
  const statusOf = (keys) => {
    const entries = keys.map((key) => byKey[key]).filter(Boolean).filter((row) => row.total > 0);
    if (!entries.length) return { status: 'ikke_relevant', kilde: t('rcd.auto.noAccountsInArea', 'Ingen kontoer i området') };
    if (entries.some((row) => row.status === 'avvik')) return { status: 'avvik', kilde: entries.map((row) => row.label + ': ' + row.statusLabel).join(', ') };
    if (entries.some((row) => row.status === 'venter_periodeslutt')) return { status: 'venter', kilde: entries.map((row) => row.label + ': ' + row.statusLabel).join(', ') };
    if (entries.every((row) => ['ok', 'ikke_relevant'].includes(row.status))) return { status: 'ok', kilde: entries.map((row) => row.label + ': ' + row.statusLabel).join(', ') };
    return { status: 'venter', kilde: entries.map((row) => row.label + ': ' + row.statusLabel).join(', ') };
  };
  const value = (id) => completeness.find((entry) => entry.id === id);
  const bankDoc = value('bankNoDoc');
  const incomplete = reports.filter((report) => !report.dataComplete).length;
  const changed = rows.filter((row) => row.basisChanged);
  const unexplainedAnalytics = safeArray(analytics?.rows).filter((row) => !row.forklaring).length;

  return {
    'A. Omfang og avstemminger': [
      { id: 'auto-a1', label: t('rcd.auto.a1', 'Alle relevante avstemmingsområder er identifisert.'), status: 'ok', kilde: t('rcd.auto.a1Source', '{count} områder identifisert ut fra klientoppsettet', { count: areaCount }) },
      { id: 'auto-a2', label: t('rcd.auto.a2', 'Alle bankkontoer er avstemt.'), ...statusOf(['bank']) },
      { id: 'auto-a3', label: t('rcd.auto.a3', 'Kunde- og leverandørreskontro er avstemt.'), ...statusOf(['kundefordringer', 'leverandorgjeld']) },
      { id: 'auto-a4', label: t('rcd.auto.a4', 'MVA er kontrollert når dette er relevant for perioden.'), ...statusOf(['mva']) },
      { id: 'auto-a5', label: t('rcd.auto.a5', 'Lønn og offentlige avgifter er kontrollert når dette er relevant.'), ...statusOf(['lonn', 'skatt']) },
      { id: 'auto-a6', label: t('rcd.auto.a6', 'Alle vesentlige balanseområder er behandlet.'), ...statusOf(['eiendeler', 'lan', 'egenkapital', 'ovrig']) },
    ],
    'B. Fullstendighet og periodisering': [
      {
        id: 'auto-b2', label: t('rcd.auto.b2', 'Banktransaksjoner uten dokumentasjon er fulgt opp.'),
        status: bankDoc && bankDoc.value ? 'avvik' : 'ok',
        kilde: bankDoc ? t('rcd.auto.b2Source', '{count} banktransaksjoner uten bilag', { count: bankDoc.value }) : t('rcd.auto.b2NoBank', 'Ingen bankavstemming i perioden'),
      },
    ],
    'C. Analytisk kontroll': [
      {
        id: 'auto-c1', label: t('rcd.auto.c1', 'Resultatutviklingen er sammenlignet med relevant sammenligningsgrunnlag.'),
        status: safeArray(analytics?.rows).length ? 'ok' : 'ikke_relevant',
        kilde: safeArray(analytics?.rows).length
          ? t('rcd.auto.c1Source', '{count} nøkkeltall sammenlignet mot {basis}', { count: analytics.rows.length, basis: String(analytics.rows[0].basisLabel || '').toLowerCase() })
          : t('rcd.auto.c1NoLines', 'Ingen resultatlinjer i perioden'),
      },
    ],
    'D. Rapportering': [
      {
        id: 'auto-d1', label: t('rcd.auto.d1', 'Rapporteringsgrunnlaget bygger på et uendret avstemmingsgrunnlag.'),
        status: changed.length ? 'avvik' : 'ok',
        kilde: changed.length ? t('rcd.cross.changedAfterApproval', '{areas} er endret etter godkjenning', { areas: changed.map((row) => row.label).join(', ') }) : t('rcd.auto.d1Unchanged', 'Ingen godkjent avstemming er endret etterpå'),
      },
      { id: 'auto-d2', label: t('rcd.auto.d2', 'Alle rapporter og plikter for perioden er identifisert.'), status: 'ok', kilde: t('rcd.auto.d2Source', '{count} rapporter/plikter identifisert', { count: reports.length }) },
      {
        id: 'auto-d3', label: t('rcd.auto.d3', 'Rapporter og spesifikasjoner har komplett datagrunnlag.'),
        status: incomplete ? 'avvik' : 'ok',
        kilde: incomplete ? t('rcd.auto.d3Source', '{count} rapporter har ufullstendig datagrunnlag', { count: incomplete }) : t('rcd.auto.d3AllComplete', 'Alle rapporter har komplett datagrunnlag'),
      },
      {
        id: 'auto-d5', label: t('rcd.auto.d5', 'Perioden er klar til kontroll.'),
        status: blockers.length ? 'blokkert' : 'ok',
        kilde: blockers.length ? t('rcd.auto.d5Blocked', 'Beregnet — se blokkeringene i konklusjonen') : t('rcd.auto.d5None', 'Ingen blokkeringer'),
      },
      {
        id: 'auto-d4', label: t('rcd.auto.d4', 'Analytiske avvik er forklart.'),
        status: unexplainedAnalytics ? 'avvik' : 'ok',
        kilde: unexplainedAnalytics ? t('rcd.auto.d4Source', '{count} analytiske avvik uten forklaring', { count: unexplainedAnalytics }) : t('rcd.auto.d4AllExplained', 'Alle sammenlikninger er forklart eller innenfor forventning'),
      },
    ],
  };
};

// `gruppe` is the stored group name (Norwegian) that auto and stored items are
// merged on; `gruppeLabel` is the label shown to the user.
const pkGroupLabel = (gruppe) => {
  switch (gruppe) {
    case 'A. Omfang og avstemminger': return t('rcd.group.scope', 'A. Omfang og avstemminger');
    case 'B. Fullstendighet og periodisering': return t('rcd.group.completeness', 'B. Fullstendighet og periodisering');
    case 'C. Analytisk kontroll': return t('rcd.group.analytical', 'C. Analytisk kontroll');
    case 'D. Rapportering': return t('rcd.group.reporting', 'D. Rapportering');
    case 'Faglige vurderinger': return t('rcd.group.professional', 'Faglige vurderinger');
    default: return gruppe;
  }
};

const pkChecklist = ({ periodAccount, auto }) => {
  const stored = safeArray(periodAccount?.checklist);
  const groups = new Map();
  for (const [gruppe, items] of Object.entries(auto)) {
    groups.set(gruppe, items.map((item) => ({ ...item, rolle: 'auto', kritisk: false, comment: '' })));
  }
  for (const item of stored) {
    const gruppe = item.gruppe || 'Faglige vurderinger';
    if (!groups.has(gruppe)) groups.set(gruppe, []);
    groups.get(gruppe).push({ ...item, rolle: 'faglig' });
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], 'nb-NO'))
    .map(([gruppe, items]) => ({ gruppe, gruppeLabel: pkGroupLabel(gruppe), items }));
};

const pkSummary = (items) => {
  const list = safeArray(items);
  const behandlet = list.filter((item) => item.status && item.status !== 'ikke_kontrollert').length;
  return {
    total: list.length,
    behandlet,
    bestatt: list.filter((item) => item.status === 'ok').length,
    avvik: list.filter((item) => item.status === 'avvik').length,
    venter: list.filter((item) => item.status === 'venter').length,
    blokkert: list.filter((item) => item.status === 'blokkert').length,
  };
};

// ------------------------------------------------------------------
// Blokkeringer
// ------------------------------------------------------------------
// Hva som står i veien for at perioden er klar. Gruppert per kildeområde, slik
// at man kan gå rett dit — en flat liste ville krevd at man selv fant ut hvor
// forholdet hørte hjemme.
const pkBlockers = ({ rows, completeness, analytics, vatTerm, periodEnded }) => {
  const groups = [];
  for (const row of rows) {
    const items = [];
    if (row.status === 'avvik') items.push(t('rcd.blocker.deviation', '{count} konto(er) står som avvik', { count: row.avvik }));
    if (row.missingDocs) items.push(t('rcd.blocker.missingDocs', '{count} konto(er) mangler dokumentasjon', { count: row.missingDocs }));
    if (row.apnePoster) items.push(t('rcd.blocker.openItems', '{count} åpne avstemmingsposter', { count: row.apnePoster }));
    if (row.status === 'venter_periodeslutt' && vatTerm?.dateTo) items.push(t('rcd.blocker.waitingTermEnd', 'Venter på periodeslutt — terminen er over {date}', { date: vatTerm.dateTo }));
    if (row.basisChanged) items.push(t('rcd.blocker.basisMoved', 'Grunnlaget har flyttet seg etter godkjenning'));
    if (items.length) groups.push({ gruppe: row.label, areaKey: row.key, sec: 'omrader', items });
  }
  const inbox = completeness.find((entry) => entry.id === 'inbox');
  if (inbox && inbox.value) groups.push({ gruppe: t('rcd.blocker.inboxGroup', 'Innboks'), areaKey: null, sec: 'oversikt', items: [t('rcd.blocker.inboxItems', '{count} dokumenter er ikke behandlet', { count: inbox.value })] });
  const missingExplanations = safeArray(analytics?.rows).filter((row) => !row.forklaring);
  if (missingExplanations.length) {
    groups.push({
      gruppe: t('rcd.blocker.analyticalGroup', 'Analytisk kontroll'), areaKey: null, sec: 'analytisk',
      items: [t('rcd.blocker.explanationsMissing', '{count} forklaringer mangler ({areas})', { count: missingExplanations.length, areas: missingExplanations.map((row) => row.label.toLowerCase()).join(', ') })],
    });
  }
  if (!periodEnded) groups.push({ gruppe: t('rcd.blocker.periodGroup', 'Perioden'), areaKey: null, sec: 'oversikt', items: [t('rcd.blocker.periodNotEnded', 'Perioden er ikke avsluttet ennå')] });
  return groups;
};

Object.assign(window, { pkMasterControls, pkCrossChecks, pkAutoChecks, pkChecklist, pkSummary, pkBlockers });
